import React from "react";
import { Pressable, Text, View } from "react-native";

import { messageSharedStyles as styles } from "./MessageShared.styles";

type MessageListItemProps = {
  title: string;
  preview: string;
  timestamp: string;
  selected: boolean;
  unread: boolean;
  onPress: () => void;
  accessibilityLabel: string;
  onSecondaryAction?: () => void;
  secondaryActionLabel?: string;
  secondaryActionIcon?: React.ReactNode;
  secondaryActionDisabled?: boolean;
  hidePreviewRow?: boolean;
  showActiveBadge?: boolean;
  categoryLabel?: string;
  categoryTone?: "lead" | "patient";
};

const MessageListItem: React.FC<MessageListItemProps> = ({
  title,
  preview,
  timestamp,
  selected,
  unread,
  onPress,
  accessibilityLabel,
  onSecondaryAction,
  secondaryActionLabel,
  secondaryActionIcon,
  secondaryActionDisabled = false,
  hidePreviewRow = false,
  showActiveBadge = false,
  categoryLabel,
  categoryTone,
}) => {
  const normalizedPreview = (preview || "").trim() || "No messages yet.";

  return (
    <Pressable
      style={styles.listItemPressable}
      onPress={onPress}
      accessibilityLabel={accessibilityLabel}
    >
      <View
        style={[
          styles.listItemContainer,
          selected && styles.listItemContainerSelected,
        ]}
      >
        <View style={styles.listItemHeader}>
          <Text numberOfLines={1} style={styles.listItemTitle}>
            {title}
          </Text>
          <View style={styles.listItemHeaderActions}>
            <Text numberOfLines={1} style={styles.listItemTimestamp}>
              {timestamp}
            </Text>
            {onSecondaryAction && secondaryActionIcon ? (
              <Pressable
                style={[
                  styles.listItemIconActionButton,
                  secondaryActionDisabled && styles.listItemIconActionDisabled,
                ]}
                onPress={onSecondaryAction}
                disabled={secondaryActionDisabled}
                accessibilityLabel={secondaryActionLabel}
              >
                {secondaryActionIcon}
              </Pressable>
            ) : null}
          </View>
        </View>

        {(showActiveBadge || !!categoryLabel) && (
          <View style={styles.listItemStatusRow}>
            {showActiveBadge ? (
              <View
                style={[
                  styles.listItemStatusBadge,
                  styles.listItemStatusBadgeActive,
                ]}
              >
                <Text style={styles.listItemStatusBadgeText}>ACTIVE</Text>
              </View>
            ) : null}
            {categoryLabel ? (
              <View
                style={[
                  styles.listItemStatusBadge,
                  categoryTone === "lead"
                    ? styles.listItemStatusBadgeLead
                    : styles.listItemStatusBadgePatient,
                ]}
              >
                <Text style={styles.listItemStatusBadgeText}>
                  {categoryLabel}
                </Text>
              </View>
            ) : null}
          </View>
        )}

        {!hidePreviewRow ? (
          <View style={styles.listItemPreviewRow}>
            <Text numberOfLines={2} style={styles.listItemPreviewText}>
              {normalizedPreview}
            </Text>
            {unread ? <View style={styles.listItemUnreadDot} /> : null}
          </View>
        ) : null}
      </View>
    </Pressable>
  );
};

export default MessageListItem;
